import { useEffect, useState } from "react";
import { formatarData, pegarHorario } from "../../../components/utils/functions";
import MobileSearchBar from "../../ui/MobileSearchBar";
import MobileFilterSheet from "../../ui/MobileFilterSheet";
import MobileList from "../../ui/MobileList";
import MobileListItem from "../../ui/MobileListItem";
import MobileSkeleton from "../../ui/MobileSkeleton";
import MobileEmpty from "../../ui/MobileEmpty";
import MobileError from "../../ui/MobileError";
import { useLogsSearch } from "./useLogsSearch";
import { MobileLogDetailSheet } from "./MobileLogDetailSheet";
import { logTargetLabel } from "./logTargetNames";

export function logSubtitle(log) {
  const parts = [
    logTargetLabel(log?.target),
    log?.targetId,
    log?.user?.email,
    log?.createdAt ? formatarData(log.createdAt) : "",
    log?.createdAt ? pegarHorario(log.createdAt) : "",
  ];
  return parts.filter(Boolean).join(" · ");
}

export default function MobileLogsList({ user }) {
  const { logs, loading, error, filters, setFilters, search, refresh } = useLogsSearch(user);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(filters);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const canSearch = Boolean(filters.from?.trim() && filters.to?.trim());

  useEffect(() => {
    setHasSearched(true);
    search();
  }, [search]);

  const openFilters = () => {
    setDraftFilters(filters);
    setFiltersOpen(true);
  };

  const applyFilters = () => {
    setFilters(draftFilters);
    setFiltersOpen(false);
    if (draftFilters.from?.trim() && draftFilters.to?.trim()) {
      setHasSearched(true);
      search(draftFilters);
    }
  };

  const handleSearch = (rawQ) => {
    if (!canSearch) {
      return;
    }
    const q = typeof rawQ === "string" ? rawQ : "";
    const next = { ...filters, q };
    setFilters(next);
    setHasSearched(true);
    search(next);
  };

  const openDetail = (log) => {
    setSelectedLog(log);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedLog(null);
  };

  const activeFilterCount =
    (filters.target ? 1 : 0) + (filters.from ? 1 : 0) + (filters.to ? 1 : 0);

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b border-primary/30 bg-mgs-bg px-4 py-2">
        <button
          type="button"
          onClick={() => {
            if (!canSearch) return;
            setHasSearched(true);
            refresh();
          }}
          disabled={!canSearch}
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-primary/50 px-3 text-sm font-medium text-laranjinha/80 hover:bg-monao disabled:cursor-not-allowed disabled:opacity-50"
        >
          Atualizar
        </button>
      </div>

      <MobileSearchBar
        value={filters.q}
        onChange={(q) => setFilters((current) => ({ ...current, q }))}
        onSubmit={handleSearch}
        submitLabel="Pesquisar"
        submitDisabled={!canSearch}
        placeholder="E-mail, nome ou ID do alvo"
      />

      <div className="flex flex-wrap items-center gap-2 border-b border-primary/30 bg-mgs-bg px-4 py-2">
        <button
          type="button"
          onClick={openFilters}
          className="inline-flex min-h-[36px] items-center rounded-full border border-primary/50 bg-monao px-3 text-sm text-laranjinha/80 hover:bg-monao"
        >
          Filtros{activeFilterCount ? ` (${activeFilterCount})` : ""}
        </button>
        {filters.target ? (
          <span className="inline-flex min-h-[36px] items-center rounded-full bg-primary/15 px-3 text-sm text-primary">
            {logTargetLabel(filters.target)}
          </span>
        ) : null}
        {filters.from && filters.to ? (
          <span className="inline-flex min-h-[36px] items-center rounded-full bg-primary/15 px-3 text-sm text-primary">
            {filters.from} — {filters.to}
          </span>
        ) : null}
      </div>

      {loading ? (
        <div aria-label="Carregando logs" role="status">
          <MobileSkeleton lines={5} />
        </div>
      ) : error ? (
        <MobileError
          message={error.message ?? "Erro ao buscar logs."}
          onRetry={refresh}
        />
      ) : hasSearched && logs.length === 0 ? (
        <MobileEmpty
          title="Nenhum log encontrado"
          message={
            filters.q?.trim()
              ? `Nenhum resultado para “${filters.q.trim()}”.`
              : "Ajuste os filtros ou toque em Pesquisar para buscar."
          }
        />
      ) : logs.length > 0 ? (
        <MobileList>
          {logs.map((item) => (
            <MobileListItem
              key={item._id}
              title={logTargetLabel(item.target)}
              subtitle={logSubtitle(item)}
              onClick={() => openDetail(item)}
            />
          ))}
        </MobileList>
      ) : null}

      <MobileFilterSheet
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="Filtros"
      >
        <div className="space-y-4">
          <label className="block text-sm font-medium text-laranjinha/80">
            Alvo
            <select
              value={draftFilters.target}
              onChange={(e) =>
                setDraftFilters((current) => ({ ...current, target: e.target.value }))
              }
              className="mt-1 w-full min-h-[44px] rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha"
            >
              <option value="">Todos</option>
              <option value="user">Usuários</option>
              <option value="product">Produtos</option>
              <option value="store">Lojas</option>
              <option value="gradablemodel">Modelos</option>
              <option value="serial">Seriais</option>
            </select>
          </label>
          <label className="block text-sm font-medium text-laranjinha/80">
            De (dd/mm/aaaa)
            <input
              type="text"
              placeholder="dd/mm/aaaa"
              value={draftFilters.from}
              onChange={(e) =>
                setDraftFilters((current) => ({ ...current, from: e.target.value }))
              }
              className="mt-1 w-full min-h-[44px] rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha"
            />
          </label>
          <label className="block text-sm font-medium text-laranjinha/80">
            Até (dd/mm/aaaa)
            <input
              type="text"
              placeholder="dd/mm/aaaa"
              value={draftFilters.to}
              onChange={(e) =>
                setDraftFilters((current) => ({ ...current, to: e.target.value }))
              }
              className="mt-1 w-full min-h-[44px] rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha"
            />
          </label>
          <button
            type="button"
            onClick={applyFilters}
            className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-primary px-4 font-medium text-mgs-bg hover:bg-[#b47c06]"
          >
            Aplicar filtros
          </button>
        </div>
      </MobileFilterSheet>

      <MobileLogDetailSheet
        open={detailOpen}
        onClose={closeDetail}
        log={selectedLog}
      />
    </div>
  );
}
